import { client as braintreeClient, dataCollector } from 'braintree-web';
import { telemetryIncrementAction } from 'core-events/actions';
import { useEffect, useState } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { PayPalService } from './PayPalService';
import { getBraintreeClientAuthorization } from './actions';

const logger = LoggerFactory.create('useBraintreeDeviceData');

// Never let a slow SDK or authorization call hold up the entire checkout render.
const COLLECTION_TIMEOUT_MS = 5000;

export type BraintreeDeviceDataState = { status: 'pending' } | { status: 'ready'; deviceData?: string };

/**
 * Collects Braintree's device data (the Kount fraud device fingerprint).
 * This needs to be passed to Recurly so the data is available to Braintree downstream.
 */
export function useBraintreeDeviceData(): BraintreeDeviceDataState {
    // Mock PayPal has no Braintree gateway, so there is nothing to fingerprint; start ready.
    const [state, setState] = useState<BraintreeDeviceDataState>(() =>
        PayPalService.getConfig().useMockPayPal ? { status: 'ready' } : { status: 'pending' },
    );

    useEffect(() => {
        if (PayPalService.getConfig().useMockPayPal) {
            return;
        }

        let settled = false;

        const settle = (deviceData?: string): void => {
            if (settled) {
                return;
            }
            settled = true;
            setState({ status: 'ready', ...(deviceData ? { deviceData } : {}) });
        };

        const timeoutId = setTimeout(() => {
            if (settled) {
                return;
            }
            logger.warn('Braintree device data collection timed out; continuing without it');
            void telemetryIncrementAction('signup.checkout.braintree.device_data.timeout');
            settle();
        }, COLLECTION_TIMEOUT_MS);

        async function collect(): Promise<void> {
            try {
                const authorization = await getBraintreeClientAuthorization();
                const clientInstance = await braintreeClient.create({ authorization });
                const collector = await dataCollector.create({ client: clientInstance });
                settle(collector.deviceData);
            } catch (error) {
                if (settled) {
                    // If the timeout (or an unmount) already settled this session, don't log or double-count.
                    return;
                }
                logger.error({ error }, 'Failed to collect Braintree device data');
                void telemetryIncrementAction('signup.checkout.braintree.device_data.error');
                settle();
            } finally {
                clearTimeout(timeoutId);
            }
        }

        void collect();

        return () => {
            // Prevent a late resolution or the timeout from setting state after unmount.
            settled = true;
            clearTimeout(timeoutId);
        };
    }, []);

    return state;
}
